import { create } from "zustand";
import { persist } from "zustand/middleware";

export type SubscriptionPlan = "free" | "premium";
export type SubscriptionInterval = "quarterly" | "halfyearly" | "yearly" | "lifetime";

export interface SubscriptionLimits {
  maxUniversities: number;
  maxStorageMB: number;
  // Weekly limits (free)
  maxCompressPerWeek: number;
  maxAutoAddPerWeek: number;
  // Daily limits
  maxAutoAddPerDay: number;
  maxDownloadsPerDay: number;
  maxExportsPerDay: number;
  maxFeedbackPerDay: number;
  maxInboxAIPerDay: number;
  canUseAI: boolean;
  canSendDM: boolean;
}

export const FREE_LIMITS: SubscriptionLimits = {
  maxUniversities: 7,
  maxStorageMB: 30,
  maxCompressPerWeek: 1,
  maxAutoAddPerWeek: 1,
  maxAutoAddPerDay: 1, // kept for backward compat but weekly is enforced
  maxDownloadsPerDay: 3,
  maxExportsPerDay: 1,
  maxFeedbackPerDay: 1,
  maxInboxAIPerDay: 0, // locked for free
  canUseAI: false,
  canSendDM: false,
};

export const PREMIUM_LIMITS: SubscriptionLimits = {
  maxUniversities: Infinity,
  maxStorageMB: 200,
  maxCompressPerWeek: Infinity,
  maxAutoAddPerWeek: Infinity,
  maxAutoAddPerDay: 4,
  maxDownloadsPerDay: Infinity,
  maxExportsPerDay: Infinity,
  maxFeedbackPerDay: 4,
  maxInboxAIPerDay: 4,
  canUseAI: true,
  canSendDM: true,
};

export const PRICING = {
  quarterly: 199, // ₹199 for 3 months
  halfyearly: 349, // ₹349 for 6 months
  yearly: 599, // ₹599 for 1 year
};

export const TESTIMONIAL_REWARD_DAYS = 14; // 14 days free trial for testimonial

interface SubscriptionState {
  plan: SubscriptionPlan;
  interval: SubscriptionInterval | null;
  expiresAt: string | null;
  currentStorageMB: number;
  limits: SubscriptionLimits;

  // Actions
  setPlan: (plan: SubscriptionPlan, interval?: SubscriptionInterval, expiresAt?: string) => void;
  setCurrentStorage: (mb: number) => void;
  checkExpiry: () => boolean; // Returns true if subscription is still valid

  // Permission checks
  canAddUniversity: (currentCount: number) => boolean;
  canAddDocument: (fileSizeMB: number) => boolean;
  canUseAIAnalysis: () => boolean;
  canUseCompression: () => boolean;
  canSendDMRequest: () => boolean;
  isPremium: () => boolean;
  isExpired: () => boolean;
  getDaysRemaining: () => number | null; // Returns days remaining or null if not premium/lifetime

  // For locked documents (when premium expires)
  isStorageExceeded: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      plan: "free",
      interval: null,
      expiresAt: null,
      currentStorageMB: 0,
      limits: FREE_LIMITS,

      setPlan: (plan, interval = null, expiresAt = null) => {
        const limits = plan === "premium" ? PREMIUM_LIMITS : FREE_LIMITS;
        set({
          plan,
          interval,
          expiresAt,
          limits,
        });
      },

      setCurrentStorage: (mb) => set({ currentStorageMB: mb }),

      checkExpiry: () => {
        const { plan, expiresAt } = get();
        if (plan !== "premium" || !expiresAt) return true;

        const now = new Date();
        const expiry = new Date(expiresAt);

        if (now > expiry) {
          // Subscription expired, revert to free
          set({
            plan: "free",
            interval: null,
            expiresAt: null,
            limits: FREE_LIMITS,
          });
          return false;
        }
        return true;
      },

      isExpired: () => {
        const { plan, expiresAt, interval } = get();
        if (plan !== "premium") return false;
        if (interval === "lifetime") return false;
        if (!expiresAt) return false;
        return new Date() > new Date(expiresAt);
      },

      canAddUniversity: (currentCount) => {
        const state = get();
        state.checkExpiry();
        return state.plan === "premium" || currentCount < state.limits.maxUniversities;
      },

      canAddDocument: (fileSizeMB) => {
        const state = get();
        state.checkExpiry();
        return state.currentStorageMB + fileSizeMB <= state.limits.maxStorageMB;
      },

      canUseAIAnalysis: () => {
        const state = get();
        state.checkExpiry();
        return state.limits.canUseAI;
      },

      canUseCompression: () => {
        const state = get();
        state.checkExpiry();
        return true; // All users can compress; free users limited to 1/week via canPerformAction
      },

      canSendDMRequest: () => {
        const state = get();
        state.checkExpiry();
        return state.limits.canSendDM;
      },

      isPremium: () => {
        const state = get();
        state.checkExpiry();
        return state.plan === "premium";
      },

      getDaysRemaining: () => {
        const state = get();
        if (state.plan !== "premium" || !state.expiresAt) return null;
        if (state.interval === "lifetime") return null; // Lifetime has no expiry

        const now = new Date();
        const expiry = new Date(state.expiresAt);
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays > 0 ? diffDays : 0;
      },

      isStorageExceeded: () => {
        const state = get();
        return state.currentStorageMB > state.limits.maxStorageMB;
      },
    }),
    {
      name: "subscription-storage",
    },
  ),
);
